import React, { useState } from "react";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router";
import Info from "../components/modals/Info";
import LoadingButton from "../components/LoadingButton";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  updateProfile,
} from "firebase/auth";
import { auth, db } from "../authentication/firebase";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { alertActions } from "../store/alertSlice";
import { useDispatch } from "react-redux";
import BackdropWrapper from "../components/modals/BackdropWrapper";
import logo from "../assets/wroot-logo.png";
import { Link } from "react-router-dom";
import signupImg from "../assets/signup-dk.png";
import signupMobile from "../assets/signup-mobile.jpg";
import SignInWithoutEmail from "../authentication/SignInWithoutEmail";
import { FaEnvelope, FaLock, FaUser, FaEyeSlash } from "react-icons/fa";
import { FaEye } from "react-icons/fa";
import { motion } from "framer-motion";
import MainNav from "../components/navbar/MainNav";

export default function Signup() {
  const [inputs, setInputs] = useState({
    email: "",
    password: "",
    fullName: "",
  });
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [backdrop, setBackdrop] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const location = useLocation();

  let { from } = location.state || { from: "/" };

  function inputChangeHandler(event) {
    setInputs((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));
  }
  const submitHandler = async (e) => {
    e.preventDefault();
    
    if (!agreeToTerms) {
      dispatch(
        alertActions.setAlert({
                      title: "Please agree to the Terms and Conditions and Privacy Policy",
          messageType: "warning",
        })
      );
      return;
    }
    
    setLoading(true);
    try {
      const response = await createUserWithEmailAndPassword(
        auth,
        inputs.email,
        inputs.password
      );
      
      await updateProfile(response.user, {
        displayName: inputs.fullName
      });
      
      await sendEmailVerification(response.user);
      dispatch(
        alertActions.setAlert({
          title: "Verify Email",
          messageType: "info",
        })
      );
      // setBackdrop(true);
      navigate(from)
    } catch (e) {
      dispatch(
        alertActions.setAlert({
          title: JSON.parse(JSON.stringify(e)).code,
          messageType: "error",
        })
      );
      console.log("Failed to create an account", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledSignup>
      <MainNav className="main-nav" />
      {/* <BackdropWrapper
        backdropHandler={() => {
          setBackdrop(false);
        }}
        element={
          <Info
            heading="Email Verification"
            msg="Email verification link has been sent to your email, please verify and login."
            backdropHandler={() => {
              setBackdrop(false);
              navigate("/login");
            }}
          />
        }
        smallSize={true}
        open={backdrop}
      /> */}
      <motion.div
        className="left"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <img src={signupImg} alt="" />
      </motion.div>
      <motion.div
        className="right"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <div className="content">
          <motion.img
            className="logo"
            src={logo}
            alt=""
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          />
          <motion.div
            className="register-form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <div className="heading">
              <h3>Hey, Planter!</h3>
              <p className="subheading">
                Help us nurture forests for a sustainable future.
              </p>
            </div>
            <form onSubmit={submitHandler}>
              <div className="input-wrapper">
                <FaUser className="input-icon" />
                <input
                  type="text"
                  name="fullName"
                  placeholder="Full Name"
                  value={inputs.fullName}
                  onChange={inputChangeHandler}
                  required
                />
              </div>
              <div className="input-wrapper">
                <FaEnvelope className="input-icon" />
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={inputs.email}
                  onChange={inputChangeHandler}
                  required
                />
              </div>
              <div className="input-wrapper">
                <FaLock className="input-icon" />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  value={inputs.password}
                  onChange={inputChangeHandler}
                  minLength={8}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              <div className="terms-checkbox">
                <input
                  type="checkbox"
                  id="agree-terms"
                  checked={agreeToTerms}
                  onChange={(e) => setAgreeToTerms(e.target.checked)}
                  required
                />
                <label htmlFor="agree-terms">
                  I agree to the{" "}
                  <Link to="/terms-of-service" target="_blank" className="terms-link">
                    Terms and Conditions
                  </Link>
                  {" "}and{" "}
                  <Link to="/privacy-policy" target="_blank" className="terms-link">
                    Privacy Policy
                  </Link>
                </label>
              </div>
              <LoadingButton loading={loading} type="submit">
                Signup{" "}
              </LoadingButton>
            </form>
            <SignInWithoutEmail />
            <div className="link-p">
              <span>Already have an account?{"  "}</span>
              <Link className="link" to="/login" state={{ from }}>
                Login
              </Link>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </StyledSignup>
  );
}

const StyledSignup = styled.section`
  height: 100vh;
  display: flex;
  align-items: center;
  text-align: center;
  overflow: hidden;
  position: relative;
  width: 100%;

  .main-nav {
    position: absolute;
    top: 20px;
    left: 0;
    right: 0;
    z-index: 100;
  }

  .left {
    flex-basis: 60%;
    height: 100vh;
    overflow: hidden;
    position: relative;
    z-index: 10;
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: right;
    }
  }
  .right {
    display: flex;
    flex-direction: column;
    flex-basis: 40%;
    align-items: center;
    justify-content: center;
    .content {
      width: 90%;
      background-color: white;
      padding-top: 30px;
      border-radius: 20px;
      .heading {
        h3 {
          margin-bottom: 0.5rem;
          color: var(--text-light-color);
          font-weight: 900;
        }
        .subheading {
          margin-bottom: 2rem;
        }
      }
      .link-p {
        margin-top: 2rem;
        span {
          text-decoration: none;
        }
        a {
          font-weight: 600;
          text-decoration: underline;
          cursor: pointer;
          margin-bottom: 1rem;
          color: var(--text-light-color);
        }
      }
      .logo {
        height: 40px;
      }
      .register-form {
        margin: 0 auto;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        background: linear-gradient(
          rgb(255, 255, 255, 0.9),
          rgb(255, 255, 255, 0.1) 95%
        );
        padding: 5% 0;
        border-radius: var(--radius-20);
        form {
          display: grid;
          grid-template-rows: repeat(5, 1fr);
          width: 70%;
          margin: 5% 0;
          .input-wrapper {
            position: relative;
            width: 300px;
            margin: 15px auto;
            .input-icon {
              position: absolute;
              left: 15px;
              top: 50%;
              transform: translateY(-50%);
              color: var(--gray-light-color);
              font-size: 1.2rem;
              z-index: 5;
            }
            input {
              width: 100%;
              padding-left: 45px;
              padding-right: 45px;
              box-sizing: border-box;
              position: relative;
            }
            .password-toggle {
              position: absolute;
              right: 8px;
              top: 15%;
              transform: translateY(-50%);
              color: var(--gray-light-color);
              font-size: 1rem;
              cursor: pointer;
              padding: 6px;
              display: flex;
              align-items: center;
              justify-content: center;
              width: 28px;
              height: 28px;
              transition: all 0.2s ease;
              z-index: 10;
              background: none;
              border: none;
              
              &:hover {
                color: var(--primary-color);
                background-color: rgba(133, 206, 51, 0.1);
                transform: translateY(-50%) scale(1.05);
              }
              
              &:focus {
                outline: none;
                color: var(--primary-color);
                background-color: rgba(133, 206, 51, 0.15);
              }
              
              &:active {
                transform: translateY(-50%) scale(0.95);
              }
            }
          }
          .terms-checkbox {
            margin: 8px auto 15px auto;
            width: 300px;
            display: flex;
            align-items: flex-start;
            gap: 10px;
            text-align: left;
            font-size: 0.9rem;
            
            input[type="checkbox"] {
              margin-top: 2px;
              width: auto;
              padding: 0;
            }
            
            label {
              color: var(--text-light-color);
              line-height: 1.4;
              
              .terms-link {
                color: var(--primary-color);
                text-decoration: underline;
                font-weight: 600;
                
                &:hover {
                  opacity: 0.8;
                }
              }
            }
          }
          
          button {
            margin: 15px auto;
            width: 300px;
          }
        }
      }
    }
  }
  @media (max-width: 1000px) {
    background: url(${signupMobile}) no-repeat center center;
    background-size: cover;
    .left {
      display: none;
    }
    .right {
      flex-basis: 100%;
      .register-form {
        background: linear-gradient(
          rgb(255, 255, 255, 0.95),
          rgb(255, 255, 255, 0.8) 95%
        );
      }
    }
  }
  @media (max-width: 768px) {
    .right .content {
      width: 100%;
      .register-form {
        /* width: 95%; */

        form {
          width: 100%;
          .input-wrapper {
            width: 260px;
          }
          .terms-checkbox {
            width: 260px;
          }
          button {
            width: 260px;
          }
        }
      }
    }
  }
`;
